import React, { useEffect } from 'react';
import { BrowserRouter, Redirect, Route, Switch } from 'react-router-dom';
import Login from './pages/Login';
import PrivateRoute from './PrivateRoute';
import Home from './pages/Home';
import Authentication from './services/authentication';
import { useSelector } from 'react-redux';
import { UserType } from './stores/authenticated';
import { clearLegacyUserIdCookie } from './services/api';
import { authSessionLog } from './services/auth-session-logger';
import { bootAuthenticatedApp, isAuthenticatedSessionRestored, recoverSessionFromHttpOnlyCookiesWithRetry } from './services/session-bootstrap';
import store, { AppState } from './stores';
import { loggedOut } from './stores/authenticated';
import { appEnv } from './config/env';
import { DepartmentType } from './stores/departments';
import { MeetingType } from './stores/meetings';
import { TalkingType } from './stores/talkings';
import { appInitialize } from './stores/app-status';
import Logout from './pages/Logout';
import Room from './components/Room/Room';
import ModalChangePassword from './presentations/ModalChangePassword';
import ForgotPassword from './pages/ForgotPassword';
import ForgotPasswordSended from './pages/ForgotPasswordSended';
import ResetPassword from './pages/ResetPassword';
import ResetPasswordComplete from './pages/ResetPasswordComplete';
import PopupStoreContext from './containers/PopupStoreContext';
import ModalCreateRoomNow from './presentations/ModalCreateRoomNow';
import AdminRoute from './AdminRoute';
import SystemManagerUsers from './pages/SystemManagerUsers';
import SystemManagerDepartments from './pages/SystemManagerDepartments';
import SystemManagerSetting from './pages/SystemManagerSetting';
import { Sound } from './services/sound';
import WebSocket from './WebSocket';
import { SystemType } from './stores/system';
import MyProfileModal from './components/MyProfile/MyProfileModal';
import MyProfileAvatarSelectModal from './components/MyProfile/MyProfileAvatarSelectModal';
import HistoryModal from './components/History/HistoryModal';
import RoomConnecting from './components/Room/RoomConnecting';

export type InfoType = {
  talkings: TalkingType[];
  meetings: MeetingType[];
  departments: DepartmentType[];
  users: UserType[];
  privacy: any[];
  totalUsers: number;
  presentUsers: number;
  absentUsers: number;
  activeMeetings: number;
  meetingsLimit: number;
  usersLimit: number;
};

/** useEffect の二重実行（Strict Mode）で並行ブートがぶつからないよう直列化 */
let appBootstrapChain: Promise<void> = Promise.resolve();

const App: React.FC = () => {
  const init = useSelector((state: AppState) => state.appState.init);
  const authentication = new Authentication();

  useEffect(() => {
    const runBootstrap = async (): Promise<void> => {
      try {
        clearLegacyUserIdCookie();

        const auth = store.getState().authenticated;
        const appInit = store.getState().appState.init;

        if (isAuthenticatedSessionRestored()) {
          return;
        }

        const bootUserId = auth.userId as number | null;
        const bootExpiresAt = auth.accessTokenExpiresAt;

        if (authentication.isSessionValid(bootExpiresAt) && bootUserId != null) {
          if (await bootAuthenticatedApp(bootUserId, auth.user)) {
            return;
          }
        }

        if (await recoverSessionFromHttpOnlyCookiesWithRetry()) {
          return;
        }

        if (!appInit) {
          authSessionLog({
            level: 'warn',
            event: 'soft_logout',
            reason: 'bootstrap',
            trigger: 'App.bootstrap',
            detail: 'session recovery failed on startup',
          });
          store.dispatch(loggedOut());
          store.dispatch(appInitialize());
        }
      } catch {
        clearLegacyUserIdCookie();
        window.location.href = appEnv.dir;
      }
    };

    appBootstrapChain = appBootstrapChain.then(() => runBootstrap()).catch(() => {});
    Sound.init();
  }, []);

  return (
    <>
      {init ? (
        <WebSocket>
          <>
            <PopupStoreContext>
              <BrowserRouter basename={appEnv.dir}>
                <Switch>
                  <Route path="/login" exact component={Login} />
                  <Route path="/logout" exact component={Logout} />
                  <Route path="/forgot-password" exact component={ForgotPassword} />
                  <Route path="/forgot-password-sended" exact component={ForgotPasswordSended} />
                  <Route path="/reset-password" exact component={ResetPassword} />
                  <Route path="/reset-password-complete" exact component={ResetPasswordComplete} />
                  <Route path="/room" exact component={Room} />
                  <Route path="/room-connecting" exact component={RoomConnecting} />
                  <PrivateRoute path="/" exact component={Home} />
                  <AdminRoute
                    path="/system-manager"
                    exact
                    render={() => <Redirect to={'/system-manager/users'} />}
                  />
                  <AdminRoute path="/system-manager/users" exact component={SystemManagerUsers} />
                  <AdminRoute
                    path="/system-manager/users/:departmentId"
                    exact
                    component={SystemManagerUsers}
                  />
                  <AdminRoute path="/system-manager/departments" exact component={SystemManagerDepartments} />
                  <AdminRoute path="/system-manager/setting" exact component={SystemManagerSetting} />
                </Switch>
              </BrowserRouter>
            </PopupStoreContext>
            <MyProfileModal />
            <MyProfileAvatarSelectModal />
            <ModalChangePassword />
            <ModalCreateRoomNow />
            <HistoryModal />
          </>
        </WebSocket>
      ) : null}
    </>
  );
};

export default App;
